// path: frontend/src/components/Modals/ProjectModal.jsx
/**
 * Карточка проекта (создание / редактирование / просмотр). RBAC-флаги
 * берутся с сервера: capabilities и project.permissions.
 * Экономические поля рендерятся при capabilities.seeEconomics; правка — под
 * permissions.canEditContractNumber / canEditHourlyRate.
 *
 * Поля формы вынесены в ProjectFormFields, чтобы их можно было переиспользовать
 * в TemplateModal при создании шаблонов. Это гарантирует идентичный интерфейс
 * и автоматическое обновление при изменении формы проекта.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useForm } from "../../hooks/useForm";
import { useDataHelpers } from "../../hooks/useDataHelpers";
import { useConfirm } from "../../context/ConfirmContext";
import { useControlledTab } from "../../hooks/useControlledTab";
import { useStableModalHeight } from "../../hooks/useStableModalHeight";
import { useEntityFiles } from "../../hooks/useEntityFiles";
import {
  useChatUnreadCount,
  useChatTotalCount,
} from "../../hooks/useChatStats";
import { normalizeFormStatus } from "../../hooks/useTaskFormState";
import { useAuth } from "../../hooks/useAuth";
import {
  usePriorityOptions,
  useAuthorFallbackDomain,
} from "../../hooks/useMetaHelpers";
import { useProjectQuery } from "../../api/queries/projects";
import { useProjectTasksQuery } from "../../api/queries/tasks";

import { ModalShell } from "../ModalShell";
import { Tabs } from "../Tabs";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import { TaskTable } from "../TaskTable";
import { HistoryTab } from "../HistoryTab";
import { ProjectChat } from "../ProjectChat";
import { FileManager } from "../FileManager";
import { ProjectAccessModal } from "./ProjectAccessModal";
import { ProjectFooterActions } from "./ProjectFooterActions";
import { TemplateSelect } from "../Templates/TemplateSelect";
import { ProjectFormFields } from "./ProjectModal/ProjectFormFields";

import { PROJECT_STATUSES, DIALOGS } from "../../utils/constants";
import { ENTITY_FIELDS_META } from "../../utils/templateSchemas";

import { TODAY, iso, addDays, fmtDMY, uid } from "../../utils/date";
import { isArchived } from "../../utils/entityState";
import { buildAuthorSubtitle } from "../../utils/entityAuthor";
import { getProjectColor } from "../../utils/projectHelpers";
import {
  unitOptionsForPtype,
  sanitizeUnitIdsForPtype,
} from "../../utils/projectUnits";
import { collectTaskPayloads } from "../../utils/templateNesting";
import { applyTemplatePayload } from "../../utils/templateSchemas";
import { buildApprovalHint } from "../../utils/assignmentApproval";
import { parseTagsInput } from "../../utils/tags";

import {
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} from "../../api/queries/projects";
import { useInstantiateTemplateMutation } from "../../api/queries/templates";
import {
  useDictionariesQuery,
  useAddDictionaryValueMutation,
} from "../../api/queries/dictionaries";

const CONTRACT_NUMBER_RE = /^[\p{L}\p{N}\-/.\s]+$/u;

const PROJECT_META = ENTITY_FIELDS_META.project;

const FORM_FIELDS = Object.freeze([
  ...Object.keys(PROJECT_META).map((f) => PROJECT_META[f].formField ?? f),
  "status",
]);

const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (Array.isArray(v?.items)) return v.items;
  return [];
};

const applyPtypeChange = (prev, nextPtype, db) => {
  const next = { ...prev, ptype: nextPtype };
  next.unitIds = sanitizeUnitIdsForPtype(next.unitIds, next.ptype, db);

  if (next.ptype === "admin") {
    next.contractNumber = "";
    next.hourlyRate = "";
    if (!["high", "mid", "low"].includes(next.priority)) {
      next.priority = "mid";
    }
  } else {
    if (!["AOG", "CRIT", "NORM"].includes(next.priority)) {
      next.priority = "NORM";
    }
  }

  return next;
};

function buildInitialValues({ existing, isCopy, copySource, ur }) {
  if (existing) {
    return {
      ...existing,
      status: normalizeFormStatus(existing, "active"),
      tagsInput: Array.isArray(existing.tags) ? existing.tags.join(", ") : "",
      contractNumber: existing.contractNumber ?? "",
      hourlyRate: existing.hourlyRate ?? "",
    };
  }
  if (isCopy) {
    return {
      ...copySource,
      id: "p_" + uid(),
      managerId: "",
      status: "active",
      archived: false,
      archivedAt: null,
      closedAt: null,
      creatorId: ur.id,
      history: [
        {
          ts: Date.now(),
          who: ur.id,
          text: `Скопирован из «${copySource.name}»`,
        },
      ],
      tagsInput: Array.isArray(copySource.tags)
        ? copySource.tags.join(", ")
        : "",
      contractNumber: "",
      hourlyRate: "",
    };
  }
  return {
    id: "p_" + uid(),
    code: "",
    name: "",
    desc: "",
    tagsInput: "",
    unitIds: [],
    managerId: "",
    start: TODAY,
    end: iso(addDays(new Date(), 30)),
    status: "active",
    plannedHours: 100,
    contractNumber: "",
    hourlyRate: "",
    color: "#64748b",
    ptype: "prod",
    longterm: false,
    archived: false,
    archivedAt: null,
    closedAt: null,
    creatorId: ur.id,
    customer: "",
    aircraftType: "",
    projectType: "",
    priority: "NORM",
    history: [{ ts: Date.now(), who: ur.id, text: "Проект создан" }],
  };
}

export const ProjectModal = (props) => {
  const { projectId, copyFromId, onClose } = props;

  const { data: fetchedProject, isLoading: isProjectLoading } = useProjectQuery(
    projectId,
    { enabled: !!projectId },
  );
  const { data: fetchedCopySource } = useProjectQuery(copyFromId, {
    enabled: !!copyFromId,
  });

  if (projectId && isProjectLoading && !fetchedProject) {
    return (
      <ModalShell title="Загрузка..." onClose={onClose} width={900}>
        <div className="modal-loading">Загрузка данных проекта...</div>
      </ModalShell>
    );
  }

  return (
    <ProjectModalContent
      {...props}
      fetchedProject={fetchedProject}
      fetchedCopySource={fetchedCopySource}
    />
  );
};

const ProjectModalContent = ({
  db,
  ur,
  projectId,
  initialTab = "info",
  copyFromId,
  returnToProjectId,
  returnToTaskId,
  returnToTag = null,
  highlightFileId = null,
  highlightFolderId = null,
  onClose,
  onSave,
  onDelete,
  onCopy,
  onChangeReq,
  openTag,
  toast,
  openTask,
  onTabChange,
  fetchedProject,
  fetchedCopySource,
}) => {
  const { capabilities } = useAuth();
  const tasksArr = useMemo(() => asArray(db.tasks), [db.tasks]);
  const projectsArr = useMemo(() => asArray(db.projects), [db.projects]);
  const employeesArr = useMemo(() => asArray(db.employees), [db.employees]);

  const normalizedDb = useMemo(
    () => ({
      ...db,
      tasks: tasksArr,
      projects: projectsArr,
      employees: employeesArr,
    }),
    [db, tasksArr, projectsArr, employeesArr],
  );

  const { empName, getTaskSpent } = useDataHelpers(normalizedDb);
  const { confirm } = useConfirm();

  const createMutation = useCreateProjectMutation();
  const updateMutation = useUpdateProjectMutation();
  const deleteMutation = useDeleteProjectMutation();
  const instantiateTemplateMutation = useInstantiateTemplateMutation();

  const { data: dictionaries = {} } = useDictionariesQuery();
  const addDictValue = useAddDictionaryValueMutation();

  const aircraftTypes = useMemo(() => {
    const set = new Set(dictionaries.aircraft_types || []);
    projectsArr.forEach((p) => {
      if (p.aircraftType) set.add(p.aircraftType.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
  }, [dictionaries.aircraft_types, projectsArr]);

  const customers = useMemo(
    () => dictionaries.customers || [],
    [dictionaries.customers],
  );
  const categories = useMemo(
    () => dictionaries.project_categories || [],
    [dictionaries.project_categories],
  );

  const existing =
    fetchedProject ||
    (projectId ? projectsArr.find((p) => p.id === projectId) || null : null);
  const copySource =
    fetchedCopySource ||
    (copyFromId ? projectsArr.find((p) => p.id === copyFromId) || null : null);

  const isCopy = !existing && !!copySource;
  const isNew = !existing;
  const readOnly = !!(existing && isArchived(existing));

  const canEditFields =
    !readOnly &&
    (existing
      ? Boolean(existing?.permissions?.canEdit)
      : capabilities.createProject);

  const canEditContractNumber =
    !readOnly &&
    Boolean(
      existing
        ? existing.permissions?.canEditContractNumber
        : capabilities.editProjectEconomics && capabilities.createProject,
    );

  const canEditHourlyRate =
    !readOnly &&
    Boolean(
      existing
        ? existing.permissions?.canEditHourlyRate
        : capabilities.editProjectEconomics && capabilities.createProject,
    );

  const canChangeStatus =
    !readOnly && existing && Boolean(existing?.permissions?.canChangeStatus);
  const canChangeManager =
    !readOnly &&
    (existing ? capabilities.manageManager : capabilities.createProject);
  const canCreateFromProject = capabilities.createProject;
  const canManageAccess =
    !readOnly && existing && Boolean(existing?.permissions?.canEdit);
  const canSeeEconomicsFields = capabilities.seeEconomics;
  const isManager = !!(existing && existing.managerId === ur.id);
  const canRequestChange = !readOnly && isManager && !!onChangeReq;

  const isViewOnlyViaTag = !!(
    existing &&
    returnToTag &&
    !canEditFields &&
    !isManager &&
    !canManageAccess
  );

  const [appliedTemplateName, setAppliedTemplateName] = useState(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [pendingTemplateTasks, setPendingTemplateTasks] = useState(() =>
    isCopy && copySource
      ? collectTaskPayloads(
          tasksArr.filter((t) => t.projectId === copySource.id && !t.archived),
        )
      : [],
  );
  const [isSaving, setIsSaving] = useState(false);

  const savingRef = useRef(false);
  const valuesRef = useRef(null);

  const [activeTab, handleTabChange] = useControlledTab(
    initialTab,
    onTabChange,
  );
  const bodyRef = useStableModalHeight(activeTab);

  const {
    values,
    handleChange: rawHandleChange,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
    setValues,
    isValid,
    isDirty,
  } = useForm(
    buildInitialValues({ existing, isCopy, copySource, ur }),
    useCallback(
      (values) => {
        const errors = {};
        if (!values.name?.trim()) errors.name = "Название обязательно";
        if (!values.code?.trim()) errors.code = "Код обязателен";
        if (!values.start) errors.start = "Дата начала обязательна";

        if (!values.customer?.trim()) {
          errors.customer = "Заказчик обязателен";
        } else if (!customers.includes(values.customer)) {
          errors.customer = "Выберите заказчика из списка";
        }

        const isAdmin = values.ptype === "admin";
        if (!isAdmin) {
          if (!values.aircraftType) errors.aircraftType = "Выберите тип АТ";
          if (!values.projectType) {
            errors.projectType = "Выберите категорию";
          } else if (!categories.includes(values.projectType)) {
            errors.projectType = "Выберите категорию из списка";
          }
          if (!values.managerId) errors.managerId = "Ответственный обязателен";
          if (!values.unitIds || values.unitIds.length === 0)
            errors.unitIds = "Выберите хотя бы одно КБ";
          if (!values.end) errors.end = "Дата окончания обязательна";
        }
        const planFilled =
          values.plannedHours !== "" &&
          values.plannedHours !== null &&
          values.plannedHours !== undefined;
        if (isAdmin) {
          if (
            planFilled &&
            (isNaN(+values.plannedHours) ||
              +values.plannedHours < 0 ||
              +values.plannedHours > 1000000)
          ) {
            errors.plannedHours = "Плановые часы: число от 0 до 1 000 000";
          }
        } else if (
          !planFilled ||
          isNaN(+values.plannedHours) ||
          +values.plannedHours < 0 ||
          +values.plannedHours > 1000000
        ) {
          errors.plannedHours = "Плановые часы обязательны (0 … 1 000 000)";
        }
        const contractRaw = String(values.contractNumber ?? "").trim();
        if (contractRaw) {
          if (contractRaw.length < 3)
            errors.contractNumber = "Номер договора - не короче 3 символов";
          else if (!CONTRACT_NUMBER_RE.test(contractRaw))
            errors.contractNumber = "Недопустимые символы в номере договора";
        }
        if (values.hourlyRate !== "" && values.hourlyRate != null) {
          const rate = Number(values.hourlyRate);
          if (!Number.isFinite(rate) || rate < 0 || rate > 1000000)
            errors.hourlyRate = "Ставка - число от 0 до 1 000 000";
        }
        const tagParse = parseTagsInput(values.tagsInput);
        if (tagParse.invalid.length > 0)
          errors.tagsInput = `Недопустимые метки: ${tagParse.invalid.join(", ")}`;
        if (!values.priority) errors.priority = "Приоритет обязателен";
        if (!values.status) errors.status = "Статус обязателен";
        return errors;
      },
      [customers, categories],
    ),
    { fields: FORM_FIELDS },
  );

  const lastSyncedIdRef = useRef(existing?.id);
  useEffect(() => {
    if (existing && existing.id !== lastSyncedIdRef.current) {
      lastSyncedIdRef.current = existing.id;
      setValues(
        buildInitialValues({ existing, isCopy: false, copySource: null, ur }),
      );
    }
  }, [existing, ur, setValues]);

  const {
    localFiles,
    localFolders,
    handleMoveFile,
    handleMoveFolder,
    handleRenameFile,
    handleFileUpload,
    handleFileDelete,
    handleCreateFolder,
    handleDeleteFolder,
  } = useEntityFiles({
    existing,
    ownerType: "project",
    toast,
    confirm,
  });

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const isAdminProject = values.ptype === "admin";

  const aircraftTypeOptions = useMemo(
    () => aircraftTypes.map((v) => ({ value: v, label: v })),
    [aircraftTypes],
  );

  const customerOptions = useMemo(
    () => customers.map((v) => ({ value: v, label: v })),
    [customers],
  );

  const categoryOptions = useMemo(
    () => categories.map((v) => ({ value: v, label: v })),
    [categories],
  );

  const handleChange = (field, value) => {
    if (field === "ptype") {
      setValues((prev) => applyPtypeChange(prev, value, normalizedDb));
      return;
    }
    rawHandleChange(field, value);
  };

  const handleAddAircraftType = useCallback(() => {
    const value = String(valuesRef.current.aircraftType ?? "").trim();
    if (!value || aircraftTypes.includes(value)) return;

    addDictValue.mutate(
      { key: "aircraft_types", value },
      {
        onSuccess: () =>
          toast(`Тип «${value}» добавлен в справочник`, "success"),
        onError: (err) => toast(err.message || "Ошибка добавления", "error"),
      },
    );
  }, [aircraftTypes, toast, addDictValue]);

  const aircraftFooter = useMemo(() => {
    if (!capabilities.editRoles || !canEditFields) return null;
    const current = String(values.aircraftType ?? "").trim();
    if (!current || aircraftTypes.includes(current)) return null;
    return (
      <button
        type="button"
        className="custom-select-add-btn"
        onClick={handleAddAircraftType}
      >
        <Ic d={ICONS.plus} size={13} /> Добавить «{current}» в справочник
      </button>
    );
  }, [
    capabilities.editRoles,
    canEditFields,
    values.aircraftType,
    aircraftTypes,
    handleAddAircraftType,
  ]);

  const chatUnread = useChatUnreadCount(ur.id, values.id, null);
  const chatTotal = useChatTotalCount(values.id, null);

  const fallbackDomain = useAuthorFallbackDomain();

  const authorId = existing?.creatorId;
  const projectCode = existing?.code;
  const projectName = existing?.name;

  const modalSubtitle = useMemo(
    () =>
      buildAuthorSubtitle({
        creatorId: authorId,
        employees: employeesArr,
        label: "Проект составил",
        fallbackDomain,
        emailSubject: projectCode ? `${projectCode}_${projectName}` : undefined,
      }),
    [authorId, projectCode, projectName, employeesArr, fallbackDomain],
  );

  const draftTasks = useMemo(() => {
    if (!isNew || pendingTemplateTasks.length === 0) return [];

    const flat = [];
    let idx = 0;
    const visit = (nodes, depth) => {
      for (const node of nodes) {
        flat.push({
          id: `draft_${idx++}`,
          _draft: true,
          title: node.title || "Без названия",
          assigneeId: null,
          status: "new",
          plannedHours: node.plannedHours ?? null,
          priority: node.priority || "mid",
          deadline: null,
          logs: [],
          projectId: values.id,
          depth,
        });
        if (Array.isArray(node.subtasks)) {
          visit(node.subtasks, depth + 1);
        }
      }
    };
    visit(pendingTemplateTasks, 0);
    return flat;
  }, [isNew, pendingTemplateTasks, values.id]);

  const projectTasksQuery = useProjectTasksQuery(
    existing?.id,
    {
      mode: "list",
      limit: 5000,
      includeArchived: true,
      includeDrafts: true,
    },
    { enabled: !!existing?.id },
  );

  const serverProjectTasks = useMemo(
    () => asArray(projectTasksQuery.data?.items),
    [projectTasksQuery.data],
  );

  const applyTemplate = useCallback(
    (template) => {
      if (!template) {
        setAppliedTemplateName(null);
        setPendingTemplateTasks([]);
        return;
      }
      const { tasks: nestedTasks, ...projectFields } = applyTemplatePayload(
        "project",
        template.payload,
      );

      if (projectFields.managerId) {
        const emp = employeesArr.find(
          (e) => e.id === projectFields.managerId && !e.fired,
        );
        if (!emp) {
          toast(
            "Ответственный из шаблона недоступен — выберите вручную",
            "warning",
          );
          delete projectFields.managerId;
        }
      }

      if (Array.isArray(projectFields.unitIds)) {
        const ptype = projectFields.ptype ?? values.ptype;
        const valid = sanitizeUnitIdsForPtype(
          projectFields.unitIds,
          ptype,
          normalizedDb,
        );
        if (valid.length !== projectFields.unitIds.length) {
          toast(
            "Часть подразделений из шаблона недоступна для этого типа проекта",
            "warning",
          );
        }
        projectFields.unitIds = valid;
      }

      if (
        projectFields.customer &&
        !customers.includes(projectFields.customer)
      ) {
        toast(
          "Заказчик из шаблона отсутствует в справочнике — выберите вручную",
          "warning",
        );
        delete projectFields.customer;
      }

      if (
        projectFields.aircraftType &&
        !aircraftTypes.includes(projectFields.aircraftType)
      ) {
        toast(
          "Тип ВС из шаблона отсутствует в справочнике — выберите вручную",
          "warning",
        );
        delete projectFields.aircraftType;
      }

      if (
        projectFields.projectType &&
        !categories.includes(projectFields.projectType)
      ) {
        toast(
          "Категория из шаблона отсутствует в справочнике — выберите вручную",
          "warning",
        );
        delete projectFields.projectType;
      }

      Object.keys(projectFields).forEach((field) => {
        if (field === "tags") {
          setFieldValue(
            "tagsInput",
            Array.isArray(projectFields.tags)
              ? projectFields.tags.join(", ")
              : "",
          );
          return;
        }
        if (field === "hourlyRate" && projectFields.hourlyRate != null) {
          setFieldValue("hourlyRate", String(projectFields.hourlyRate));
          return;
        }
        setFieldValue(field, projectFields[field]);
      });
      setPendingTemplateTasks(Array.isArray(nestedTasks) ? nestedTasks : []);
      setAppliedTemplateName(template.name);
    },
    [
      setFieldValue,
      setAppliedTemplateName,
      setPendingTemplateTasks,
      employeesArr,
      customers,
      aircraftTypes,
      categories,
      normalizedDb,
      values.ptype,
      toast,
    ],
  );

  const buildSavePayload = useCallback(
    (vals, overrides = {}) => {
      const isAdminPtype = vals.ptype === "admin";
      const result = {};

      if (canEditFields) {
        Object.assign(result, vals);
        result.description = vals.desc;
        result.startDate = vals.start;
        result.endDate = vals.end;
        result.color = getProjectColor(vals);
        result.plannedHours =
          vals.plannedHours === "" || vals.plannedHours == null
            ? null
            : String(vals.plannedHours);
        result.managerId = vals.managerId || null;
        result.tags = parseTagsInput(vals.tagsInput).valid;

        const STRIP = [
          "tagsInput",
          "desc",
          "start",
          "end",
          "id",
          "creatorId",
          "history",
          "files",
          "folders",
          "photos",
          "access",
          "archived",
          "archivedAt",
          "closedAt",
          "contractNumber",
          "hourlyRate",
        ];
        for (const key of STRIP) delete result[key];
      }

      if (canEditContractNumber) {
        result.contractNumber = isAdminPtype
          ? null
          : String(vals.contractNumber ?? "").trim() || null;
      }
      if (canEditHourlyRate) {
        result.hourlyRate = isAdminPtype
          ? null
          : vals.hourlyRate === "" || vals.hourlyRate == null
            ? null
            : String(vals.hourlyRate);
      }

      return { ...result, ...overrides };
    },
    [canEditFields, canEditContractNumber, canEditHourlyRate],
  );

  const saveHandler = useCallback(
    async (vals) => {
      if (savingRef.current) return;
      savingRef.current = true;
      setIsSaving(true);
      try {
        const payload = buildSavePayload(vals);
        let result;
        try {
          if (isNew) {
            result = await createMutation.mutateAsync(payload);
          } else {
            result = await updateMutation.mutateAsync({
              id: existing.id,
              body: payload,
              version: existing.version,
            });
          }
        } catch (e) {
          if (e.status === 409 && e.details?.needsConfirmation) {
            const ok = await confirm({
              title: "Конфликт данных",
              message: e.message,
              confirmLabel: "Продолжить",
            });
            if (!ok) return;
            if (isNew) {
              result = await createMutation.mutateAsync(payload);
            } else {
              result = await updateMutation.mutateAsync({
                id: existing.id,
                body: payload,
                needsConfirmation: true,
              });
            }
          } else {
            throw e;
          }
        }

        if (pendingTemplateTasks.length > 0 && result?.id) {
          try {
            const res = await instantiateTemplateMutation.mutateAsync({
              projectId: result.id,
              parentTaskId: null,
              payloads: pendingTemplateTasks,
            });
            if (res) {
              const created = res.created ?? 0;
              const failed = res.failed ?? 0;
              const drafts = Array.isArray(res.warnings)
                ? res.warnings.length
                : 0;
              const active = Math.max(0, created - drafts);

              if (failed > 0) {
                const parts = [];
                if (active > 0) parts.push(`активно ${active}`);
                if (drafts > 0) parts.push(`в черновиках ${drafts}`);
                parts.push(`с ошибками ${failed}`);
                toast(`Проект создан. Задачи: ${parts.join(", ")}`, "warning");
              } else if (drafts > 0) {
                toast(
                  `Проект создан. Задачи: активно ${active}, в черновиках ${drafts}`,
                  "warning",
                );
              } else if (created > 0) {
                toast(`Проект и ${created} задач(и) созданы`, "success");
              }
            }
          } catch (err) {
            toast(
              err?.message || "Проект создан, задачи — с ошибкой",
              "warning",
            );
          }
        }
        onSave(result, isNew);
      } catch (err) {
        toast(err.message || "Ошибка сохранения проекта", "error");
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [
      isNew,
      existing,
      buildSavePayload,
      createMutation,
      updateMutation,
      pendingTemplateTasks,
      instantiateTemplateMutation,
      toast,
      onSave,
      confirm,
      setIsSaving,
    ],
  );

  const saveAsDraftHandler = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const payload = buildSavePayload(valuesRef.current, { status: "draft" });
      let result;
      if (isNew) {
        result = await createMutation.mutateAsync(payload);
      } else {
        result = await updateMutation.mutateAsync({
          id: existing.id,
          body: payload,
          version: existing.version,
        });
      }
      onSave(result, isNew);
    } catch (err) {
      toast(err.message || "Ошибка сохранения черновика", "error");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    isNew,
    existing,
    buildSavePayload,
    createMutation,
    updateMutation,
    onSave,
    toast,
    setIsSaving,
  ]);

  const deleteHandler = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      if (!(await confirm(DIALOGS.deleteProject(existing.name)))) return;
      await deleteMutation.mutateAsync(existing.id);
      toast("Проект удалён", "success");
      onDelete(existing.id);
    } catch (err) {
      toast(err.message || "Ошибка удаления проекта", "error");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [existing, onDelete, confirm, deleteMutation, toast, setIsSaving]);

  const taskList = useMemo(() => {
    if (!existing) return [];
    const projectIsArchived = isArchived(existing);
    const source = projectTasksQuery.isSuccess
      ? serverProjectTasks
      : tasksArr.filter((t) => t.projectId === projectId);

    const filtered = source.filter((t) => projectIsArchived || !isArchived(t));

    const ids = new Set(filtered.map((t) => t.id));
    const byParent = new Map();
    for (const t of filtered) {
      const pId = t.parentTaskId;
      const key = pId && ids.has(pId) ? pId : null;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(t);
    }

    const result = [];
    const visited = new Set();
    const visit = (parentId, depth) => {
      const children = byParent.get(parentId) || [];
      for (const child of children) {
        if (visited.has(child.id)) continue;
        visited.add(child.id);
        result.push({ ...child, depth });
        visit(child.id, depth + 1);
      }
    };

    visit(null, 0);

    for (const t of filtered) {
      if (!visited.has(t.id)) {
        visited.add(t.id);
        result.push({ ...t, depth: 0 });
      }
    }

    return result;
  }, [
    serverProjectTasks,
    tasksArr,
    projectId,
    existing,
    projectTasksQuery.isSuccess,
  ]);

  const displayedTasks = useMemo(
    () => [...draftTasks, ...taskList],
    [draftTasks, taskList],
  );

  const tabs = [
    { id: "info", label: "Информация" },
    { id: "tasks", label: `Задачи (${displayedTasks.length})` },
    { id: "chat", label: "Чат проекта", count: chatTotal, badge: chatUnread },
    ...(existing
      ? [{ id: "files", label: `Вложения (${localFiles.length})` }]
      : []),
    ...(existing ? [{ id: "hist", label: "История" }] : []),
  ];

  const employeeOptions = useMemo(
    () => [
      { value: "", label: "- Выберите ответственного -" },
      ...employeesArr
        .filter((e) => !e.fired)
        .map((e) => ({ value: e.id, label: `${e.last} ${e.first}` })),
    ],
    [employeesArr],
  );

  const unitOptions = useMemo(
    () => unitOptionsForPtype(values.ptype, normalizedDb),
    [values.ptype, normalizedDb],
  );
  const priorityOptions = usePriorityOptions(values.ptype);
  const statusOptions = useMemo(
    () =>
      Object.entries(PROJECT_STATUSES).map(([k, v]) => ({
        value: k,
        label: v,
      })),
    [],
  );

  const candidates = useMemo(() => {
    const ids = new Set(
      tasksArr
        .filter((t) => t.projectId === values.id)
        .map((t) => t.assigneeId)
        .filter(Boolean),
    );
    if (values.managerId) ids.add(values.managerId);
    return [...ids]
      .map((id) => employeesArr.find((e) => e.id === id))
      .filter(Boolean);
  }, [tasksArr, employeesArr, values.id, values.managerId]);

  const approvalHint = useMemo(
    () =>
      buildApprovalHint({
        user: ur,
        db: normalizedDb,
        existing,
        isNew,
        targetType: "project",
        entity: values,
      }),
    [ur, normalizedDb, existing, isNew, values],
  );

  const saveDisabled =
    isSaving ||
    !(
      canEditFields ||
      canEditContractNumber ||
      canEditHourlyRate ||
      canChangeStatus ||
      canChangeManager
    ) ||
    (isNew ? !isValid : !isValid || !isDirty);
  const showBackButton = !!(returnToProjectId || returnToTaskId);

  const modalTitle = readOnly ? (
    "Проект (Архив)"
  ) : existing ? (
    <>
      Карточка проекта{" "}
      <span className="modal-title-entity">{`«${existing.name}»`}</span>
    </>
  ) : isCopy ? (
    <>
      Копирование проекта:{" "}
      <span className="modal-title-entity">{copySource.name}</span>
    </>
  ) : (
    "Новый проект"
  );

  const requestHours =
    canRequestChange && !isAdminProject
      ? () => onChangeReq("hours", "project", values.id)
      : null;
  const requestDeadline =
    canRequestChange && !isAdminProject
      ? () => onChangeReq("deadline", "project", values.id)
      : null;

  const handleChatOpenTask = useCallback(
    (id) =>
      openTask(
        id,
        "form",
        null,
        null,
        values.id,
        "chat",
        null,
        "subtasks",
        null,
        null,
        returnToTag || null,
      ),
    [openTask, values.id, returnToTag],
  );

  return (
    <>
      <ModalShell
        title={modalTitle}
        subtitle={modalSubtitle}
        onClose={onClose}
        width={900}
        className="modal-project"
        actions={
          <ProjectFooterActions
            readOnly={readOnly}
            existing={existing}
            ur={ur}
            canCreateFromProject={canCreateFromProject}
            canManageAccess={canManageAccess}
            values={values}
            db={normalizedDb}
            toast={toast}
            onDelete={deleteHandler}
            onCopy={onCopy}
            onOpenAccess={() => setAccessOpen(true)}
            disabled={isSaving}
            onSaveAsDraft={isNew ? saveAsDraftHandler : null}
            saveAsDraftDisabled={!values.name?.trim()}
          />
        }
        saveLabel={isNew ? "Создать проект" : "Сохранить"}
        saveDisabled={saveDisabled}
        onSave={(e) => handleSubmit(saveHandler)(e)}
        bodyRef={bodyRef}
        showBack={showBackButton}
      >
        {readOnly && (
          <div className="info-box">
            {existing.archivedAt
              ? `Проект в архиве с ${fmtDMY(existing.archivedAt)}. Редактирование запрещено.`
              : `Проект ${existing.status === "cancelled" ? "отменён" : "закрыт"}. Редактирование запрещено.`}
          </div>
        )}

        {isViewOnlyViaTag && (
          <div className="info-box">{`Открыто по метке «${returnToTag}» - режим просмотра.`}</div>
        )}

        <Tabs tabs={tabs} active={activeTab} onChange={handleTabChange} />

        {activeTab === "info" && (
          <div className="project-info-layout">
            <div className="project-info-fields-wrap">
              {isNew && !isCopy && (
                <TemplateSelect kind="project" onApply={applyTemplate} />
              )}

              {isNew && appliedTemplateName && (
                <div className="info-box">
                  Применён шаблон: <b>{appliedTemplateName}</b>
                </div>
              )}

              <ProjectFormFields
                form={{ values, handleChange, errors, touched }}
                access={{
                  canEditFields,
                  isAdminProject,
                  canChangeStatus,
                  canEditContractNumber,
                  canEditHourlyRate,
                  canChangeManager,
                  canSeeEconomicsFields,
                }}
                options={{
                  customerOptions,
                  aircraftTypeOptions,
                  categoryOptions,
                  priorityOptions,
                  unitOptions,
                  employeeOptions,
                  statusOptions,
                  openTag,
                }}
                aircraftFooter={aircraftFooter}
                onRequestHours={requestHours}
                onRequestDeadline={requestDeadline}
                isTemplate={false}
              />

              {approvalHint && <div className="info-box">{approvalHint}</div>}
            </div>
          </div>
        )}

        {activeTab === "tasks" && (existing || draftTasks.length > 0) && (
          <div className="tm-block">
            <div className="subtask-header">
              <div className="rep-panel-title">Задачи проекта</div>
              {existing && (
                <button
                  className="btn primary sm"
                  onClick={() =>
                    openTask(
                      null,
                      "form",
                      null,
                      existing.id,
                      existing.id,
                      "tasks",
                      null,
                      "subtasks",
                      null,
                      null,
                      returnToTag || null,
                    )
                  }
                  disabled={readOnly}
                >
                  Создать задачу
                </button>
              )}
            </div>
            <TaskTable
              tasks={displayedTasks}
              onRowClick={(id) =>
                openTask(
                  id,
                  "form",
                  null,
                  null,
                  existing.id,
                  "tasks",
                  null,
                  "subtasks",
                  null,
                  null,
                  returnToTag || null,
                )
              }
              columns={[
                "title",
                "assignee",
                "status",
                "planned",
                "fact",
                "deadline",
              ]}
              db={normalizedDb}
              getTaskSpent={getTaskSpent}
              empName={empName}
              treeMode={true}
            />
          </div>
        )}

        {activeTab === "chat" && (
          <ProjectChat
            projectId={values.id}
            currentUser={ur}
            toast={toast}
            employees={employeesArr}
            candidates={candidates}
            openTask={handleChatOpenTask}
            tasks={tasksArr}
          />
        )}

        {activeTab === "files" && existing && (
          <FileManager
            files={localFiles}
            folders={localFolders}
            highlightFileId={highlightFileId}
            highlightFolderId={highlightFolderId}
            onUpload={handleFileUpload}
            onDelete={handleFileDelete}
            onCreateFolder={handleCreateFolder}
            onDeleteFolder={handleDeleteFolder}
            onMoveFile={handleMoveFile}
            onMoveFolder={handleMoveFolder}
            onRenameFile={handleRenameFile}
            canUpload={!readOnly && canEditFields}
            canDelete={!readOnly && canEditFields}
            employeeName={empName}
          />
        )}

        {activeTab === "hist" && existing && (
          <HistoryTab history={values.history || []} empName={empName} />
        )}
      </ModalShell>

      {accessOpen && existing && (
        <ProjectAccessModal
          db={normalizedDb}
          projectId={existing.id}
          onClose={() => setAccessOpen(false)}
          toast={toast}
        />
      )}
    </>
  );
};
